/**
 * Token Synchronization Service
 * Handles cross-tab sync and plugin communication
 * 
 * CSP-Compliant: Uses data attributes and postMessage instead of inline scripts
 */

const STORAGE_KEY = 'token';
const CHANNEL_NAME = 'wildbox-token-sync';
const PLUGIN_MESSAGE_SOURCE = 'wildbox-plugin';

class TokenSyncService {
    constructor() {
        this.channel = null;
        this.pluginActive = false;
        this.listeners = new Set();
        this.freshToken = null; // In-memory token cache
        this.init();
    }

    init() {
        // 1. Check if plugin is active (set via data attribute by content script)
        // CSP-compliant: Uses data attribute instead of window.__WILDBOX_PLUGIN_ACTIVE__
        this.pluginActive = document.documentElement.dataset.wildboxPluginActive === 'true';

        // 2. Setup BroadcastChannel for cross-tab sync (works without plugin)
        if (typeof BroadcastChannel !== 'undefined') {
            this.channel = new BroadcastChannel(CHANNEL_NAME);
            this.channel.onmessage = (event) => {
                if (event.data?.type === 'TOKEN_UPDATE' && event.data?.token) {
                    this._handleExternalTokenUpdate(event.data.token, 'broadcast');
                }
                if (event.data?.type === 'TOKEN_CLEAR') {
                    this.freshToken = null;
                    localStorage.removeItem(STORAGE_KEY);
                    sessionStorage.removeItem(STORAGE_KEY);
                    // Redirect other tabs to signin if not already there
                    if (!window.location.pathname.includes('/signin')) {
                        window.location.href = `${process.env.REACT_APP_ROUTER_BASE_PATH || ''}/signin`;
                    }
                }
            };
        }

        // 3. Listen for plugin token updates via postMessage (CSP-compliant)
        window.addEventListener('message', (event) => {
            if (event.source !== window || event.data?.source !== PLUGIN_MESSAGE_SOURCE) {
                return;
            }

            if (event.data?.type === 'PLUGIN_TOKEN_INJECT' && event.data?.token) {
                this.freshToken = event.data.token;
                this._handleExternalTokenUpdate(event.data.token, 'plugin-inject');
            }
        });

        // 4. Listen for plugin token updates (via custom event - still works from content script)
        window.addEventListener('wildbox:token-update', (event) => {
            if (event.detail?.token) {
                this._handleExternalTokenUpdate(event.detail.token, 'plugin');
            }
        });

        // 5. Listen for storage changes from other tabs
        window.addEventListener('storage', (event) => {
            if (event.key === STORAGE_KEY && event.newValue) {
                this._handleExternalTokenUpdate(event.newValue, 'storage');
            }
        });
    }

    /**
     * Get current token (checks fresh token first, then localStorage)
     */
    getToken() {
        // Fresh token from plugin or recent rotation
        if (this.freshToken) {
            return this.freshToken;
        }
        return localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
    }

    /**
     * Update token after API response rotation
     * @param {string} newToken - The new token from X-New-Authorization header
     */
    updateToken(newToken) {
        if (!newToken) return;

        const currentToken = this.getToken();
        if (currentToken === newToken) return; // No change

        // 1. Update storage
        if (localStorage.getItem(STORAGE_KEY)) {
            localStorage.setItem(STORAGE_KEY, newToken);
        }
        if (sessionStorage.getItem(STORAGE_KEY)) {
            sessionStorage.setItem(STORAGE_KEY, newToken);
        }

        // 2. Broadcast to other tabs (without plugin)
        if (this.channel) {
            this.channel.postMessage({ type: 'TOKEN_UPDATE', token: newToken });
        }

        // 3. Notify plugin (if active)
        if (this.pluginActive) {
            window.dispatchEvent(new CustomEvent('wildbox:token-rotated', {
                detail: { token: newToken }
            }));
        }

        // 4. Update in-memory reference
        this.freshToken = newToken;

        // 5. Notify internal listeners
        this._notifyListeners(newToken);
    }

    /**
     * Handle token update from external source (broadcast/plugin/storage)
     */
    _handleExternalTokenUpdate(newToken, source) {
        const currentToken = this.getToken();
        if (currentToken === newToken) return;

        console.log(`[TokenSync] Token updated from ${source}`);

        // Update local storage
        if (localStorage.getItem(STORAGE_KEY)) {
            localStorage.setItem(STORAGE_KEY, newToken);
        }
        if (sessionStorage.getItem(STORAGE_KEY)) {
            sessionStorage.setItem(STORAGE_KEY, newToken);
        }

        this.freshToken = newToken;
        this._notifyListeners(newToken);
    }

    /**
     * Clear token completely (for logout)
     * Purges in-memory cache, storage, broadcasts to other tabs, and notifies plugin
     */
    clearToken() {
        this.freshToken = null;

        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);

        // Broadcast clear to other tabs
        if (this.channel) {
            this.channel.postMessage({ type: 'TOKEN_CLEAR' });
        }

        // Notify plugin to clear its stored token
        if (this.pluginActive) {
            window.dispatchEvent(new CustomEvent('wildbox:token-cleared'));
        }
    }

    /**
     * Subscribe to token changes
     */
    onTokenChange(callback) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    _notifyListeners(token) {
        this.listeners.forEach(cb => cb(token));
    }
}

// Singleton instance
const tokenSync = new TokenSyncService();
export default tokenSync;
